import React, { forwardRef } from "react";
import { BaseLogo, LogoType } from "../../../core/models/entities/BaseLogo";

interface LogoImageProp extends React.ImgHTMLAttributes<HTMLImageElement> {
  logo: BaseLogo;
  useHighRes?: boolean;
  useDigit?: boolean;
  useTrueColors?: boolean;
  // Opt in to render a NameLogo smaller when its heightMm is below 60. Only
  // useful in preview contexts (lists, cards, detail dialogs). Do NOT enable
  // for the design marker, the logo editor canvas, or any context where the
  // image must occupy its actual physical placement footprint.
  scaleNameByHeight?: boolean;
}

// NameLogos should preview at a size that reflects their physical print height.
// Linear scale from 10mm → 0.3 up to 60mm → 1.0. Heights above 60mm render full
// size; heights below 10mm clamp to 0.3. Other logo types are unaffected.
function getNameLogoScale(logo: BaseLogo): number {
  if (logo.logoType !== LogoType.NameLogo) return 1;
  const heightMm = logo.heightMm ?? 60;
  if (heightMm >= 60) return 1;
  return Math.max(0.3, 0.3 + ((heightMm - 10) / 50) * 0.7);
}

export const LogoImage = forwardRef<HTMLImageElement, LogoImageProp>(
  (props, ref) => {
    const {
      logo,
      useHighRes,
      useDigit,
      useTrueColors,
      scaleNameByHeight,
      ...rest
    } = props;

    const { lowResUrl, highResUrl, digitUrl, trueColorsUrl } = logo.displayImage;

    rest.src =
      useTrueColors && trueColorsUrl
        ? trueColorsUrl
        : useDigit && digitUrl
          ? digitUrl
          : useHighRes && highResUrl
            ? highResUrl
            : lowResUrl;

    rest.alt = logo.title;

    const scale = scaleNameByHeight ? getNameLogoScale(logo) : 1;
    const img = <img ref={ref} {...rest} />;
    if (scale < 1) {
      // Wrap the img so the base NameLogo scale lives on the wrapper and any
      // hover-scale utilities on the img (e.g. `hover:scale-[3]`) still apply.
      // The two transforms compose multiplicatively.
      return (
        <span
          style={{
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            transform: `scale(${scale})`,
            transformOrigin: "center",
          }}
        >
          {img}
        </span>
      );
    }
    return img;
  },
);
